import { useCallback, useEffect, useState } from 'react';

import { scopeHash } from '@/guidance/progress/scope';

import { foldText } from '@/shared/utils/searchFold';

import { isSearchable, normaliseTerm } from './term';
import type { SearchPortal } from './types';

/**
 * QF-07 / D-PUX-07 — recent searches, in the only form the search map found
 * privacy-safe (search map R2; it supersedes the earlier G-3 "not built"):
 *
 * - sessionStorage, never localStorage: gone with the tab, never on disk for
 *   the next person at a shared branch device;
 * - one key per portal and per WHO is searching, by a hash of the session's
 *   opaque ids (as guide progress is keyed): one person's terms are never
 *   another's, and no id is stored;
 * - at most five TYPED terms, kept only once a result was opened from them —
 *   never a title, a number the person did not type, or an id;
 * - forgotten at sign-out, when the session ends (the shell gives way) and
 *   when another person signs in; "Clear recent searches" forgets them at once;
 * - the buying organisation and the supplier portal only, never the operator
 *   console;
 * - never sent to the server. Where storage is refused (a private window, an
 *   http host without SubtleCrypto) they live in memory for this page only.
 */
export const RECENT_LIMIT = 5;

const PREFIX = 'fidelen.search.v1.';

export function recentAllowed(portal: SearchPortal): boolean {
  return portal === 'tenant' || portal === 'vendor';
}

/** Where storage was refused: this page's memory, by portal and identity. */
const memory = new Map<string, string[]>();
/** The storage key each live portal + identity resolved to (null: memory only). */
const resolved = new Map<string, string | null>();
/** How many providers hold each portal + identity right now (StrictMode mounts twice). */
const holders = new Map<string, number>();

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function idOf(portal: SearchPortal, identity: string): string {
  return `${portal}|${identity}`;
}

function parse(raw: string | null): string[] {
  if (raw === null) return [];

  try {
    const value: unknown = JSON.parse(raw);

    return Array.isArray(value)
      ? value
          .filter((term): term is string => typeof term === 'string' && isSearchable(term))
          .slice(0, RECENT_LIMIT)
      : [];
  } catch {
    return [];
  }
}

function read(id: string, key: string | null): string[] {
  if (key === null) return memory.get(id) ?? [];

  try {
    return parse(storage()?.getItem(key) ?? null);
  } catch {
    return memory.get(id) ?? [];
  }
}

function write(id: string, key: string | null, terms: string[]): void {
  if (key !== null) {
    try {
      const store = storage();

      if (store !== null) {
        if (terms.length === 0) store.removeItem(key);
        else store.setItem(key, JSON.stringify(terms));

        return;
      }
    } catch {
      // Refused (quota, a private window): this page's memory instead.
    }
  }

  if (terms.length === 0) memory.delete(id);
  else memory.set(id, terms);
}

/** Every stored key of a portal (or of every portal), except `keep`. */
function removeStored(portal: SearchPortal | null, keep: string | null = null): void {
  try {
    const store = storage();

    if (store === null) return;

    const prefix = portal === null ? PREFIX : `${PREFIX}${portal}.`;
    const keys: string[] = [];

    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key !== null && key.startsWith(prefix) && key !== keep) keys.push(key);
    }

    for (const key of keys) store.removeItem(key);
  } catch {
    // Nothing stored that could be reached.
  }
}

/**
 * Forgets every recent search, of every portal and person, in this tab — the
 * shells call it as a sign-out starts, before the session is gone.
 */
export function forgetAllRecentSearches(): void {
  removeStored(null);
  memory.clear();
}

function forget(portal: SearchPortal, identity: string): void {
  const id = idOf(portal, identity);
  const key = resolved.get(id);

  memory.delete(id);
  resolved.delete(id);

  if (key !== undefined && key !== null) write(id, key, []);
}

export interface RecentSearches {
  /** Most recent first. */
  terms: readonly string[];
  /** Keeps a typed term, once a result has been opened from it. */
  remember: (term: string) => void;
  clear: () => void;
}

const NONE: readonly string[] = [];
const OFF: RecentSearches = { terms: NONE, remember: () => undefined, clear: () => undefined };

/**
 * This person's recent searches in this portal. Held by the shell's Quick
 * Find provider, which is keyed by identity: when it goes for good (sign-out,
 * the session ended, another person), so do the terms.
 */
export function useRecentSearches(portal: SearchPortal, identity: string): RecentSearches {
  const allowed = recentAllowed(portal);
  const id = idOf(portal, identity);
  const [state, setState] = useState<{ id: string; key: string | null; terms: readonly string[] }>({
    id: '',
    key: null,
    terms: NONE,
  });

  useEffect(() => {
    if (!allowed) return;

    let live = true;

    void scopeHash([portal, identity]).then((hash) => {
      if (!live) return;

      const key = hash === null ? null : `${PREFIX}${portal}.${hash}`;
      // Another person's terms never linger in this tab beside this one's.
      removeStored(portal, key);
      for (const other of [...memory.keys()])
        if (other.startsWith(`${portal}|`) && other !== id) memory.delete(other);
      resolved.set(id, key);
      setState({ id, key, terms: read(id, key) });
    });

    return () => {
      live = false;
    };
  }, [allowed, portal, identity, id]);

  // Forgotten when the provider is gone for good — not on React's StrictMode
  // re-mount, which puts it straight back.
  useEffect(() => {
    if (!allowed) return;

    holders.set(id, (holders.get(id) ?? 0) + 1);

    return () => {
      holders.set(id, (holders.get(id) ?? 1) - 1);
      setTimeout(() => {
        if ((holders.get(id) ?? 0) > 0) return;

        holders.delete(id);
        forget(portal, identity);
      }, 0);
    };
  }, [allowed, portal, identity, id]);

  const ready = allowed && state.id === id;

  const remember = useCallback(
    (raw: string) => {
      if (!ready) return;

      const term = normaliseTerm(raw);

      if (!isSearchable(term)) return;

      setState((current) => {
        const folded = foldText(term);
        const terms = [term, ...current.terms.filter((kept) => foldText(kept) !== folded)].slice(
          0,
          RECENT_LIMIT,
        );
        write(current.id, current.key, terms);

        return { ...current, terms };
      });
    },
    [ready],
  );

  const clear = useCallback(() => {
    setState((current) => {
      write(current.id, current.key, []);

      return { ...current, terms: NONE };
    });
  }, []);

  return ready ? { terms: state.terms, remember, clear } : OFF;
}
